// Frontend wrapper around wxcc-operations-run-search-query.
//
// Phase-0 findings (see lambda/ccutils/wxcc-ai-session-summary/standards/
// wxcc-search-*.json for the raw probes):
//
//   • GraphQL introspection is stripped (dal_search rejects __schema/__type
//     with FieldUndefined). Field names below are pinned from the API's
//     own "Schema help" validation-error hints + real data pulls.
//
//   • Wrapper shape is NOT Relay-style edges/node. Each root returns a
//     wrapper object with a named list field + pageInfo:
//       taskDetails            → { tasks: [TaskDetails], pageInfo }
//       taskLegDetails         → { taskLegs: [TaskLegDetails], pageInfo }
//       contactAIAgentSession  → { data: [ContactAIAgentSession], pageInfo }
//       agentSession           → { agentSessions: [AgentSession], pageInfo }
//     pageInfo = { endCursor, hasNextPage }.
//
//   • Only mandatory args are `from`/`to` (epoch ms). ANI/interactionId
//     filtering at the API level is NOT supported ("Unknown field argument
//     'ani'"). So the Operations Center pulls a window and filters
//     client-side. For UAT-scope volumes this is fine.
//
//   • ANI/DNIS/interactionId live on `contactAIAgentSession`, NOT on
//     `taskDetails`. TaskDetails uses `id` for the interaction identifier,
//     and it matches `contactAIAgentSession.interactionId` exactly.
//
//   • Nested types: entryPoint, flow, lastQueue, lastAgent, lastTeam,
//     lastSite, firstQueue on TaskDetails are `NameIdInfo`/`QueueInfo`/
//     `AgentInfo` objects that require `{ id name }` subselections.
//
//   • Rate limit: ~1 req per 2s ("S1 Tool rate limit exceeded. Retry
//     after 2 seconds"). Callers should serialise + throttle.

import { callTool } from './wxccMcpClient';
import { fetchAllPaged } from './wxccPagingClient';

const OPS = 'ops';
const TOOL = 'wxcc-operations-run-search-query';

/**
 * Raw pass-through. Sends a GraphQL query + optional variables to
 * run-search-query and returns whatever the MCP tool wraps.
 */
export async function runSearchQuery(query, variables) {
  return callTool(OPS, TOOL, variables ? { query, variables } : { query });
}

// The API caps at ~1 request per 2 seconds ("S1 Tool rate limit exceeded.
// Retry after 2 seconds"). Every function below that fires more than one
// runSearchQuery MUST pace calls at ≥ RATE_GAP_MS apart.
const RATE_GAP_MS = 2200;

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Fetch AI Agent sessions in a date window and optionally filter by ANI
 * client-side (the API doesn't accept an ANI arg). Returns normalised
 * rows keyed for the Operations Center call-search table, plus a
 * `truncated` flag when the API signalled hasNextPage — because the
 * API's own pagination args aren't documented and Phase 2 doesn't
 * paginate, users need to know they may be missing calls.
 *
 * @param {{ from: number, to: number, aniContains?: string }} args
 * @returns {Promise<{ rows: CallRow[], pageInfo: { endCursor: string|null, hasNextPage: boolean }, truncated: boolean }>}
 */
export async function searchAiSessions({ from, to, aniContains, returnRaw = false, onProgress, signal }) {
  // Full-window fetch via adaptive bisection — the server returns at most
  // 500 rows per call with no pagination arg, so we slice until every leaf
  // is non-truncated. For typical ANI searches this is 1-3 slices (~2-7s).
  const { rows: rawSessions, meta } = await fetchAllPaged(
    AI_SESSIONS_QUERY, 'contactAIAgentSession', 'data', 'sessionId',
    { from, to, onProgress, signal },
  );
  const rows = rawSessions.map(aiSessionToCallRow);
  const filtered = aniContains
    ? rows.filter((r) => (r.ani ?? '').includes(aniContains.trim()))
    : rows;
  return {
    rows: filtered,
    pageInfo: { endCursor: null, hasNextPage: false },
    truncated: meta.truncated,
    meta,
    ...(returnRaw ? { rawSessions } : {}),
  };
}

/**
 * Resolve one call by interactionId. Fetches (or reuses) the three roots
 * for the given window and filters client-side on interactionId.
 *
 * Callers should pass `cachedAiSessions` (the raw AI-session rows they
 * already have from the search page) to avoid a redundant fetch — that
 * takes the three-call sequence down to two, halving the rate-limit wait.
 *
 * Calls are SERIALISED with a 2.2s pause between them, per the API's
 * ~1-req/2s throttle. Total time: ~4.5s for three fetches, ~2.5s for two.
 */
export async function resolveCallByInteractionId(interactionId, { from, to, cachedAiSessions } = {}) {
  if (!interactionId) throw new Error('interactionId is required.');
  if (from == null || to == null) throw new Error('from/to (epoch ms) are required.');

  const errors = {};
  let allAiSessions = cachedAiSessions ?? null;

  if (!allAiSessions) {
    try {
      const r = await fetchAllPaged(
        AI_SESSIONS_QUERY, 'contactAIAgentSession', 'data', 'sessionId',
        { from, to },
      );
      allAiSessions = r.rows;
    } catch (e) {
      errors.aiSessions = e.message;
      allAiSessions = [];
    }
  }

  let allTasks = [];
  try {
    const r = await fetchAllPaged(
      TASK_DETAILS_QUERY, 'taskDetails', 'tasks', 'id',
      { from, to },
    );
    allTasks = r.rows;
  } catch (e) {
    errors.task = e.message;
  }

  let allLegs = [];
  try {
    const r = await fetchAllPaged(
      TASK_LEG_DETAILS_QUERY, 'taskLegDetails', 'taskLegs', 'id',
      { from, to },
    );
    allLegs = r.rows;
  } catch (e) {
    errors.legs = e.message;
  }

  const task = allTasks.find((t) => t.id === interactionId) ?? null;
  const legs = allLegs.filter((l) => l.taskId === interactionId);
  const aiSessions = allAiSessions.filter((s) => s.interactionId === interactionId);

  return {
    task: task ? taskToDetail(task) : null,
    legs: legs.map(legToDetail),
    aiSessions: aiSessions.map(aiSessionToCallRow),
    // Raw full window (unfiltered). Powers Caller journey without a
    // redundant fetch — the caller already paid for this data.
    allAiSessions,
    errors: Object.keys(errors).length ? errors : null,
  };
}

// -- GraphQL queries (all field names verified against live API 2026-09-30) --

const AI_SESSIONS_QUERY = `
  query AiSessions($from: Long!, $to: Long!) {
    contactAIAgentSession(from: $from, to: $to) {
      data {
        interactionId sessionId agentId agentName agentType agentLanguage
        agentAIEngine interactionOutcome intent channelType ani dnis
        entryPoint { id name }
        flow { id name }
        sessionStartTime sessionEndTime duration
        noOfTotalMessages averageResponseTime totalIntentCompleted
        dialogCount lastAction totalActionCompleted
        deadAirCount evalInteractionsFailureCount bargeInCount
        fulfillmentCount welcomeMessageResponseTime
        overallEvalScore wordRatioTotalScore
      }
      pageInfo { endCursor hasNextPage }
    }
  }
`;

const TASK_DETAILS_QUERY = `
  query TaskDetails($from: Long!, $to: Long!) {
    taskDetails(from: $from, to: $to) {
      tasks {
        id status channelType direction createdTime endedTime totalDuration
        firstQueueId firstQueueName
        lastQueue { id name }
        lastAgent { id name }
        lastTeam { id name }
        lastSite { id name }
        entryPoint { id name }
        terminationType terminationReason isContactHandled
        lastWrapupCodeName
      }
      pageInfo { endCursor hasNextPage }
    }
  }
`;

const TASK_LEG_DETAILS_QUERY = `
  query TaskLegDetails($from: Long!, $to: Long!) {
    taskLegDetails(from: $from, to: $to) {
      taskLegs {
        id taskId createdTime endedTime channelType direction handleType
        callLegType handleTime ringingDuration connectedDuration
        holdDuration wrapupDuration terminationReason abandonedType
        queue { id name }
        owner { id name }
        team { id name }
        site { id name }
      }
      pageInfo { endCursor hasNextPage }
    }
  }
`;

// -- Normalisation to the Operations Center Call/AiSession/Leg shapes ---

function aiSessionToCallRow(s) {
  return {
    interactionId: s.interactionId,
    sessionId: s.sessionId,
    agentId: s.agentId,
    agentName: s.agentName,
    agentType: s.agentType,
    agentEngine: s.agentAIEngine,
    channelType: s.channelType,
    ani: s.ani,
    dnis: s.dnis,
    entryPoint: s.entryPoint?.name ?? null,
    flow: s.flow?.name ?? null,
    outcome: s.interactionOutcome,
    intent: s.intent,
    lastAction: s.lastAction,
    startTs: s.sessionStartTime,
    endTs: s.sessionEndTime,
    durationMs: s.duration,
    messageCount: s.noOfTotalMessages,
    avgResponseMs: s.averageResponseTime,
    dialogCount: s.dialogCount,
    // Phase 5 fields powering concern score + UAT metrics.
    deadAirCount: s.deadAirCount,
    evalInteractionsFailureCount: s.evalInteractionsFailureCount,
    bargeInCount: s.bargeInCount,
    fulfillmentCount: s.fulfillmentCount,
    welcomeMessageResponseTime: s.welcomeMessageResponseTime,
    overallEvalScore: s.overallEvalScore,
    wordRatioTotalScore: s.wordRatioTotalScore,
  };
}

function taskToDetail(t) {
  return {
    interactionId: t.id,
    status: t.status,
    channelType: t.channelType,
    direction: t.direction,
    createdTs: t.createdTime,
    endedTs: t.endedTime,
    durationMs: t.totalDuration,
    firstQueue: t.firstQueueName ?? null,
    lastQueue: t.lastQueue?.name ?? null,
    lastAgent: t.lastAgent?.name ?? null,
    lastTeam: t.lastTeam?.name ?? null,
    lastSite: t.lastSite?.name ?? null,
    entryPoints: (t.entryPoint ?? []).map((e) => e.name).filter(Boolean),
    terminationType: t.terminationType,
    terminationReason: t.terminationReason,
    isHandled: t.isContactHandled,
    wrapupCode: t.lastWrapupCodeName,
  };
}

function legToDetail(l) {
  return {
    legId: l.id,
    interactionId: l.taskId,
    createdTs: l.createdTime,
    endedTs: l.endedTime,
    channelType: l.channelType,
    direction: l.direction,
    handleType: l.handleType,
    callLegType: l.callLegType,
    handleTimeMs: l.handleTime,
    ringingMs: l.ringingDuration,
    connectedMs: l.connectedDuration,
    holdMs: l.holdDuration,
    wrapupMs: l.wrapupDuration,
    terminationReason: l.terminationReason,
    abandonedType: l.abandonedType,
    queue: l.queue?.name ?? null,
    owner: l.owner?.name ?? null,
    team: l.team?.name ?? null,
    site: l.site?.name ?? null,
  };
}

